import { noop } from 'underscore';
import ko from 'knockout';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Label } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { HelpLink } from '@bingads-webui-react/help-link';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import BackboneCallToActionAdapter from '@bingads-webui-campaign-legacy/adextension-actionlink/view/action-type-adapter';
import BackboneLanguageAdapter from '@bingads-webui-campaign-legacy/adextension-actionlink/view/language-adapter';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { getStyles } from './style';

export const CallToActionDescription = ({
  isMMAAutomatedCTAOptInEnabled, automatedCTAEnable, i18n, isDisplayPlusMMA, isBulk,
}) => {
  if (!isMMAAutomatedCTAOptInEnabled) {
    if (isDisplayPlusMMA) {
      return (
        <ReactTemplate
          model={{
            b: text => (<b>{text}</b>),
          }}
          template={i18n.getString(_TL_('"Automated" is the default option for your call to action’s {{b}}Language{{/b}} and {{b}}Action text{{/b}}. This means that we’ll automatically choose your call to action and the language it serves in during ad delivery.'))}
        />
      );
    }
    return (
      <span>
        {i18n.getString(_TL_('We\'ve updated call to action options fields to default to Language "Automated" and Action text "Automated"'))}
      </span>
    );
  }

  const helpLinkElement = <HelpLink query="help:app54810">{i18n.getString(_TL_('Learn more'))}</HelpLink>;
  const boldFinalUrlElement = <b>{i18n.getString(_TL_('Final URL'))}</b>;
  const boldEnableMMAAutoElement = <b>{i18n.getString(_TL_('Enable autogenerated calls to action for Multimedia ads'))}</b>;

  if (!isBulk && automatedCTAEnable) {
    return (
      <>
        <span>
          {i18n.getString(_TL_('You’ve enabled auto-generated assets for multimedia ads. This setting automatically generates assets to enhance ad relevance and performance. Please note that automated call to actions are the only auto-generated assets currently supported for multimedia ads. We use information pulled from your '))}
        </span>
        {boldFinalUrlElement}
        <span>{i18n.getString(_TL_(' as well as any assets you provide to automatically generate additional assets for your ads when available. '))}</span>
        {helpLinkElement}
      </>
    );
  }

  return (
    <>
      <span>
        {
          isBulk ?
            i18n.getString(_TL_('Enable this option in your campaign settings to allow Microsoft Advertising to automatically generate assets for your multimedia ads. We pull information and content from ')) :
            i18n.getString(_TL_('You’ve opted out of auto-generated assets for multimedia ads. This means that we won’t automatically generate assets from your '))
        }
      </span>
      {boldFinalUrlElement}
      <span>{i18n.getString(_TL_(' to enhance ad relevance and performance. To enable this setting, select the checkbox next to '))}</span>
      {boldEnableMMAAutoElement}
      <span>{i18n.getString(_TL_(' in your campaign settings. '))}</span>
      {helpLinkElement}
    </>
  );
};

CallToActionDescription.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isMMAAutomatedCTAOptInEnabled: PropTypes.bool,
  automatedCTAEnable: PropTypes.bool,
  isDisplayPlusMMA: PropTypes.bool,
  isBulk: PropTypes.bool,
};

CallToActionDescription.defaultProps = {
  isMMAAutomatedCTAOptInEnabled: false,
  automatedCTAEnable: false,
  isDisplayPlusMMA: false,
  isBulk: false,
};

export const LegacyCallToActionWithLanguage = React.memo(({
  id, className, value, onChange, isDisplayPlusMMA, i18n, setNewCTACallback, isMMAAutomatedCTAOptInEnabled, automatedCTAEnable, isBulk, isMultimediaAd,
}) => {
  const ctaView = useRef();
  const langView = useRef();

  const { cta, language, ctaText } = value || {};
  const [selectedCta, setSelectedCta] = useState(cta);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [selectedCtaText, setSelectedCtaText] = useState(ctaText || cta);

  let ctaContainer;
  let langContainer;

  const viewModel = useMemo(() => ({
    ActionType: ko.observable(cta),
    Language: ko.observable(language),
  }), [cta, language]);

  useEffect(() => {
    if (setNewCTACallback) {
      setNewCTACallback(() => ({ CallToAction, CallToActionLanguage }) => {
        if (CallToActionLanguage) {
          viewModel.Language = ko.observable(CallToActionLanguage);
          viewModel.ActionType(CallToAction);
          ctaView.current.observable(CallToAction);
          ctaView.current.render();

          langView.current.observable = viewModel.Language;
          langView.current.render();
        } else {
          ctaView.current.observable(CallToAction);
          ctaView.current.render();
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNewCTACallback]);

  useEffect(() => {
    const languageAdapter = new BackboneLanguageAdapter({
      observable: viewModel.Language,
      isDisplayPlusMMA,
      isMultimediaAd,
      viewModel,
      callback: (selectedOption) => {
        setSelectedLanguage(selectedOption.id);
      },
      noEntiyCheck: true,
      isMMAAutomatedCTAOptInEnabled,
    });

    const actionTypeAdapter = new BackboneCallToActionAdapter({
      observable: viewModel.ActionType,
      isDisplayPlusMMA,
      isMultimediaAd,
      viewModel,
      callback: (selectedOption) => {
        setSelectedCta(selectedOption.id);
        setSelectedCtaText(selectedOption.value);
      },
      noEntiyCheck: true,
      isMMAAutomatedCTAOptInEnabled,
    });

    ctaView.current = actionTypeAdapter;
    ctaView.current.render();
    ctaView.current.$el.appendTo(ctaContainer);

    langView.current = languageAdapter;
    langView.current.render();
    langView.current.$el.appendTo(langContainer);

    return () => {
      langView.current.remove();
      ctaView.current.remove();
    };
    // Only init once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onChange({ cta: selectedCta, language: selectedLanguage }, { ctaText: selectedCtaText });
  }, [onChange, selectedCta, selectedCtaText, selectedLanguage]);

  const classes = useDefaultStyles(getStyles);
  const tooltipId = isDisplayPlusMMA ? 'pop_BA_AudAds_CTA_Text' : 'pop_BA_AdExtensions_ActionText';
  return (
    <div className="legacy-call-to-action-with-language-wrapper">
      <CallToActionDescription i18n={i18n} isMMAAutomatedCTAOptInEnabled={isMMAAutomatedCTAOptInEnabled} automatedCTAEnable={automatedCTAEnable} isDisplayPlusMMA={isDisplayPlusMMA} isBulk={isBulk} />
      <div
        id={id}
        className={classnames(className, classes.languageContainer)}
      >
        <Label>
          {i18n.getString(_TL_('Language'))}
          <HelpPopup aria-label={i18n.getString(_TL_('Language'))} topic="pop_BA_MMA_ActionLanguage" />
        </Label>
        <div ref={(ref) => { langContainer = ref; }} />
      </div>

      <div
        id={id}
        className={classnames(className, classes.callToActionContainer)}
      >
        <Label>
          {i18n.getString(_TL_('Action text'))}
          <HelpPopup aria-label={i18n.getString(_TL_('Action text'))} topic={tooltipId} />
        </Label>
        <div ref={(ref) => { ctaContainer = ref; }} />
      </div>
    </div>
  );
});

LegacyCallToActionWithLanguage.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.shape({
    cta: PropTypes.string,
    language: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  isDisplayPlusMMA: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setNewCTACallback: PropTypes.func,
  isMMAAutomatedCTAOptInEnabled: PropTypes.bool,
  isMultimediaAd: PropTypes.bool,
  automatedCTAEnable: PropTypes.bool,
  isBulk: PropTypes.bool,
};

LegacyCallToActionWithLanguage.defaultProps = {
  id: undefined,
  className: undefined,
  onChange: noop,
  isDisplayPlusMMA: false,
  setNewCTACallback: null,
  isMMAAutomatedCTAOptInEnabled: false,
  isMultimediaAd: false,
  automatedCTAEnable: false,
  isBulk: false,
};
